import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab
} from '@mui/material';
import {
  Info as InfoIcon,
  Science as ScienceIcon,
  Inventory as InventoryIcon,
  Description as DocIcon
} from '@mui/icons-material';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' 
  ? 'https://barebiologics-api-bdgjaefwc4h7d9gu.eastus2-01.azurewebsites.net/api'
  : 'http://localhost:8080/api');

const getStatusColor = (status) => {
  const colors = {
    'RECEIVED': 'info',
    'PROCESSING': 'warning',
    'PROCESSING_COMPLETE': 'secondary',
    'MANUFACTURED': 'primary',
    'ELIGIBILITY_PENDING': 'warning',
    'ELIGIBLE': 'success',
    'RELEASED': 'success',
    'DISCARDED': 'error',
    'QUARANTINE': 'warning',
    'PENDING': 'warning',
    'UPLOADED': 'success',
    'APPROVED': 'success'
  };
  return colors[status] || 'default';
};

const DonorDetailDialog = ({ open, onClose, donor: initialDonor }) => {
  const [donor, setDonor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Fetch full donor details when dialog opens
  useEffect(() => {
    if (open && initialDonor) {
      fetchDonorDetails();
    }
  }, [open, initialDonor]);

  const fetchDonorDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/donors/${initialDonor.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Handle both data.data and data formats
        const donorData = data.data || data;
        if (donorData) {
          // Ensure arrays exist to prevent crashes
          setDonor({
            ...donorData,
            tissues: donorData.tissues || [],
            manufacturedVials: donorData.manufacturedVials || [],
            eligibilityDocuments: donorData.eligibilityDocuments || []
          });
        }
      } else {
        // If API fails, use initial donor data
        setDonor({
          ...initialDonor,
          tissues: initialDonor.tissues || [],
          manufacturedVials: initialDonor.manufacturedVials || [],
          eligibilityDocuments: initialDonor.eligibilityDocuments || []
        });
      }
    } catch (err) {
      console.error('Error fetching donor details:', err);
      // Fallback to initial donor data on error
      setDonor({
        ...initialDonor,
        tissues: initialDonor.tissues || [],
        manufacturedVials: initialDonor.manufacturedVials || [],
        eligibilityDocuments: initialDonor.eligibilityDocuments || []
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setDonor(null);
    setActiveTab(0);
    onClose();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!initialDonor) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon color="primary" />
          <Typography variant="h5" fontWeight="bold">Donor Details</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
          <Typography variant="h6" color="primary">{initialDonor.donorId}</Typography>
          <Typography variant="body1" color="text.secondary">{initialDonor.mareName}</Typography>
          <Chip 
            label={donor?.status || initialDonor.status} 
            size="small" 
            color={getStatusColor(donor?.status || initialDonor.status)}
          />
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : donor ? (
          <>
            {/* Tabs */}
            <Tabs 
              value={activeTab} 
              onChange={(e, newValue) => setActiveTab(newValue)}
              sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
            >
              <Tab icon={<InfoIcon />} label="Overview" iconPosition="start" />
              <Tab icon={<ScienceIcon />} label="Tissues" iconPosition="start" />
              <Tab icon={<InventoryIcon />} label="Manufactured Vials" iconPosition="start" />
              <Tab icon={<DocIcon />} label="Documents" iconPosition="start" />
            </Tabs>

            {/* Tab 0: Overview */}
            {activeTab === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Receipt Information
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Date Received</Typography>
                          <Typography variant="body2">{formatDate(donor.dateReceived)}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Received By</Typography>
                          <Typography variant="body2">{donor.receivedByName || '-'}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Disposition</Typography>
                          <Typography variant="body2">{donor.disposition}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Tissue Labeled</Typography>
                          <Typography variant="body2">{donor.tissueLabeledConfirmed ? 'Yes' : 'No'}</Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Shipping Condition
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Shipper Temp</Typography>
                          <Typography variant="body2">{donor.internalShipperTemp ? `${donor.internalShipperTemp}°C` : '-'}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Wet Ice</Typography>
                          <Typography variant="body2">{donor.wetIcePresent ? 'Yes' : 'No'}</Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">Contamination</Typography>
                          <Typography variant="body2" color={donor.contaminationEvidence ? 'error.main' : 'inherit'}>
                            {donor.contaminationEvidence ? 'Yes' : 'None detected'}
                          </Typography>
                          {donor.contaminationNotes && (
                            <Typography variant="caption" color="error">{donor.contaminationNotes}</Typography>
                          )}
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Processing Information
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Started</Typography>
                          <Typography variant="body2">{formatDate(donor.processingStartedAt)}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Completed</Typography>
                          <Typography variant="body2">{formatDate(donor.processingCompletedAt)}</Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">Notes</Typography>
                          <Typography variant="body2">{donor.processingNotes || '-'}</Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Manufacturing Summary
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Total Vials Made</Typography>
                          <Typography variant="h5" color="primary">{donor.totalVialsMade || 0}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Total Cost</Typography>
                          <Typography variant="h5" color="primary">
                            ${Number(donor.totalManufacturingCost || 0).toFixed(2)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">Completed</Typography>
                          <Typography variant="body2">{formatDate(donor.manufacturingCompletedAt)}</Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                {donor.notes && (
                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Additional Notes
                        </Typography>
                        <Typography variant="body2">{donor.notes}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            )}

            {/* Tab 1: Tissues */}
            {activeTab === 1 && (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Tissue Type</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Qty Received</strong></TableCell>
                      <TableCell><strong>Qty Processed</strong></TableCell>
                      <TableCell><strong>Vials Made</strong></TableCell>
                      <TableCell><strong>Processed At</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {donor.tissues && donor.tissues.length > 0 ? (
                      donor.tissues.map((tissue) => (
                        <TableRow key={tissue.id || Math.random()}>
                          <TableCell>
                            <Chip 
                              label={tissue.tissueType === 'AM' ? 'Amniotic Membrane' : 'Umbilical Cord'} 
                              size="small"
                              color={tissue.tissueType === 'AM' ? 'primary' : 'secondary'}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip label={tissue.status || 'N/A'} size="small" color={getStatusColor(tissue.status)} />
                          </TableCell>
                          <TableCell>{tissue.quantityReceived || '-'}</TableCell>
                          <TableCell>{tissue.quantityProcessed || '-'}</TableCell>
                          <TableCell>{tissue.vialsManufactured || '-'}</TableCell>
                          <TableCell>{formatDate(tissue.processedAt)}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                            No tissues found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Tab 2: Manufactured Vials */}
            {activeTab === 2 && (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Product</strong></TableCell>
                      <TableCell><strong>SKU</strong></TableCell>
                      <TableCell><strong>Quantity</strong></TableCell>
                      <TableCell><strong>Lot Number</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Unit Cost</strong></TableCell>
                      <TableCell><strong>Total Cost</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {donor.manufacturedVials?.length > 0 ? (
                      donor.manufacturedVials.map((vial) => (
                        <TableRow key={vial.id}>
                          <TableCell>{vial.productName}</TableCell>
                          <TableCell>{vial.productSku}</TableCell>
                          <TableCell>{vial.quantity}</TableCell>
                          <TableCell>{vial.lotNumber}</TableCell>
                          <TableCell>
                            <Chip label={vial.status} size="small" color={getStatusColor(vial.status)} />
                          </TableCell>
                          <TableCell>${Number(vial.unitCost || 0).toFixed(2)}</TableCell>
                          <TableCell>${Number(vial.totalCost || 0).toFixed(2)}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                            No vials manufactured yet
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Tab 3: Documents */}
            {activeTab === 3 && (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>#</strong></TableCell>
                      <TableCell><strong>Document Type</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>File Name</strong></TableCell>
                      <TableCell><strong>Uploaded At</strong></TableCell>
                      <TableCell><strong>Uploaded By</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {donor.eligibilityDocuments && donor.eligibilityDocuments.length > 0 ? (
                      donor.eligibilityDocuments.map((doc, index) => (
                        <TableRow key={doc.id || index}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{doc.documentName || '-'}</TableCell>
                          <TableCell>
                            <Chip label={doc.status || 'N/A'} size="small" color={getStatusColor(doc.status)} />
                          </TableCell>
                          <TableCell>{doc.fileName || '-'}</TableCell>
                          <TableCell>{formatDate(doc.uploadedAt)}</TableCell>
                          <TableCell>{doc.uploadedByName || '-'}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                            No documents found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        ) : (
          <Typography color="text.secondary">Unable to load donor details</Typography>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DonorDetailDialog;

